import { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { useThemeMode } from '_metronic/partials'

interface Company {
  name: string
  color: string
  average: number
  data: number[]
}

interface JobStatisticsChartProps {
  categories: string[]
  companies: Company[]
}

const JobStatisticsChart: React.FC<JobStatisticsChartProps> = ({ categories, companies }) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const chart = new ApexCharts(chartRef.current, getChartOptions(categories, companies, mode))
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, categories, companies])

  return (
    <div
      ref={chartRef}
      id='kt_charts_job_statistics_chart'
      style={{ height: '400px' }}
      className='card-rounded-bottom'></div>
  )
}

function getChartOptions(categories: string[], companies: Company[], mode: string): ApexOptions {
  const textColor = mode === 'dark' ? '#A1A5B7' : '#7E8299'

  // Create series for both bars and lines
  const series = companies.flatMap(company => [
    // Bar series
    {
      name: company.name,
      type: 'column',
      data: company.data,
    },
    // Line series
    {
      name: `Avg. ${company.name}`,
      type: 'line',
      data: Array(categories.length)
        .fill(0)
        .map(() => company.average),
    },
  ])

  // Create color array alternating bar and line colors
  const colors = companies.flatMap(company => [company.color, company.color])

  // Title for the chart showing company averages
  const title = {
    text: companies.map(company => `${company.average}`).join('     '),
    align: 'left' as 'left',
    offsetY: 0,
    style: {
      fontSize: '24px',
      fontWeight: 'bold',
    },
  }

  // Subtitle for the chart showing company names
  const subtitle = {
    text: companies.map(company => `Avg. ${company.name} · Jan – Dec`).join('     '),
    align: 'left' as 'left',
    offsetY: 30,
    style: {
      fontSize: '14px',
      color: textColor,
    },
  }

  return {
    series,
    chart: {
      height: 450,
      type: 'line',
      stacked: false,
      toolbar: {
        show: false,
      },
      fontFamily: 'inherit',
    },
    title,
    subtitle,
    stroke: {
      width: companies.flatMap(() => [0, 3]), // 0 for bars, 3 for lines
      curve: 'straight',
      dashArray: companies.flatMap(() => [0, 0]), // No dash for solid lines
    },
    plotOptions: {
      bar: {
        columnWidth: '70%',
        borderRadius: 2,
        dataLabels: {
          position: 'top',
        },
      },
    },
    colors,
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
    },
    xaxis: {
      categories: categories,
      title: {
        text: 'Sales',
        offsetY: 10,
        style: {
          color: textColor,
        },
      },
      labels: {
        style: {
          colors: textColor,
        },
      },
      axisBorder: {
        show: false,
      },
    },
    yaxis: {
      title: {
        style: {
          color: textColor,
        },
      },
      min: 0,
      max: 40,
      tickAmount: 8,
      labels: {
        style: {
          colors: textColor,
        },
        formatter: function (val) {
          return val.toFixed(0)
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val) {
          return val.toFixed(1)
        },
      },
    },
    legend: {
      show: false,
    },
    grid: {
      borderColor: mode === 'dark' ? '#2D3448' : '#EBEDF3',
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}

export default JobStatisticsChart
